/**
 * DocumentPreviewModal — full-size preview of the retained original scan.
 *
 * Opens from the review-card thumbnail without leaving the Timesheet
 * Review workflow. Header identity comes from the CURRENT record
 * (patient/employee match), never from the upload filename. Download /
 * Open Original only render when a retained original actually exists.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowSquareOut,
  DownloadSimple,
  Spinner,
  X,
} from '@phosphor-icons/react';
import DocumentPageViewer from '../../components/timesheet/DocumentPageViewer';
import {
  downloadTimesheetOriginal,
  openTimesheetOriginal,
} from '../../hooks/useAuthedBlobUrl';

const DocumentPreviewModal = ({ ts, patientName, employeeName, onClose }) => {
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(null); // 'download' | 'open' | null
  const closeRef = useRef(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleMeta = useCallback((m) => setMeta(m), []);

  const handleDownload = async () => {
    setBusy('download');
    try {
      await downloadTimesheetOriginal(ts.id, meta?.download_filename);
      toast.success('Original timesheet downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Download failed');
    } finally {
      setBusy(null);
    }
  };

  const handleOpen = async () => {
    setBusy('open');
    try {
      await openTimesheetOriginal(ts.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not open original');
    } finally {
      setBusy(null);
    }
  };

  const extracted = ts.extracted_data || {};
  const period =
    extracted.week_start_date && extracted.week_end_date
      ? `${extracted.week_start_date} – ${extracted.week_end_date}`
      : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Original scanned timesheet preview"
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-2 sm:p-6"
      onClick={onClose}
      data-testid="document-preview-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-slate-900 rounded-xl shadow-2xl border border-slate-700 w-full max-w-6xl h-[92vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-slate-700 shrink-0 flex-wrap">
          <div className="min-w-0">
            <h2 className="text-sm font-bold text-white">Original Scanned Timesheet</h2>
            <div className="text-[11px] text-slate-400 flex items-center gap-2 flex-wrap mt-0.5">
              {patientName && <span data-testid="doc-modal-patient">Patient: {patientName}</span>}
              {employeeName && <span data-testid="doc-modal-employee">Employee: {employeeName}</span>}
              {period && <span data-testid="doc-modal-period">Service Period: {period}</span>}
              {meta?.available === false && (
                <span className="text-amber-400">Original not retained (legacy upload)</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {meta?.available && (
              <>
                <button
                  type="button"
                  onClick={handleOpen}
                  disabled={!!busy}
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-200 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-md px-2.5 py-1.5 disabled:opacity-50"
                  data-testid="doc-modal-open-original"
                  title="Open the protected original in a new tab"
                >
                  {busy === 'open' ? <Spinner size={13} className="animate-spin" /> : <ArrowSquareOut size={13} />}
                  Open Original
                </button>
                <button
                  type="button"
                  onClick={handleDownload}
                  disabled={!!busy}
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-md px-2.5 py-1.5 disabled:opacity-50"
                  data-testid="doc-modal-download"
                  title="Download the exact original uploaded file"
                >
                  {busy === 'download' ? <Spinner size={13} className="animate-spin" /> : <DownloadSimple size={13} />}
                  Download
                </button>
              </>
            )}
            <button
              type="button"
              ref={closeRef}
              onClick={onClose}
              aria-label="Close preview"
              className="p-1.5 rounded-md text-slate-300 hover:text-white hover:bg-slate-700"
              data-testid="doc-modal-close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Viewer */}
        <div className="flex-1 min-h-0">
          <DocumentPageViewer timesheetId={ts.id} onMeta={handleMeta} enableKeyboard />
        </div>

        <div className="px-4 py-1.5 border-t border-slate-800 text-[10px] text-slate-500 shrink-0 hidden sm:block">
          Esc close · ←/→ pages · Ctrl/Cmd +/− zoom · drag to pan
        </div>
      </div>
    </div>
  );
};

export default DocumentPreviewModal;
